import React, { Component } from 'react'
import { graphql, Link } from 'gatsby'
import Img from 'gatsby-image'
import SEO from '../../../components/seo'

import axios from 'axios'

import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'

import Layout from '../../../components/layout/layout'
import NextPreviousRow from '../../../components/next-previous'
import PageTitle from '../../../components/page-title'

import isUserSignedIn from '../../../auth/is-user-signed-in'

import StudentContent from '../../../components/layout/student-content'
import TeacherContentStudentAccordion from '../../../components/layout/teacher-content/teacher-content-student-accordion'
import TeacherContentTeacherAccordion from '../../../components/layout/teacher-content/teacher-content-teacher-accordion'
import Video from '../../../components/video'

// eslint-disable-next-line
import threedmssImage from '../../../queries/threedmssImage'

const Page = class extends Component {
  constructor(props) {
    super(props)

    this.state = {
      student_accordion_open: true,
      authenticated: undefined
    }

    this.axios_cancel_token = axios.CancelToken.source()
  }

  async componentDidMount() {
    const is_signed_in = isUserSignedIn()
    this.setState({ authenticated: is_signed_in })
  }

  render() {
    const { location } = this.props
    if (this.state.authenticated === undefined) {
      return (
        <React.Fragment>
          <Layout>
            <Container fluid className="d-flex h-100 flex-column pt-3">
              <Row className="mb-3 justify-content-center w-100">
                <div className="spinner-grow text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </Row>
            </Container>
          </Layout>
        </React.Fragment>
      )
    } else {
      return (
        <React.Fragment>
          <SEO title="Building Blocks of Life" />
          <Layout
            location={location}
            course="threedmss"
            setStudentAccordionState={(state) => this.setState({ student_accordion_open: state })}
            setAuthenticated={(is_signed_in) => this.setState({ authenticated: is_signed_in })}
          >
            <Container fluid className="d-flex h-100 flex-column pt-3">
              <PageTitle title="Building Blocks of Life" no_breadcrumb={true} />
              <Row style={{ marginBottom: '2rem' }}>
                {this.state.authenticated &&
                  <React.Fragment>
                    <Col xs={12} lg={6} id="student-accordion-column">
                      <TeacherContentStudentAccordion
                        title="Student View"
                        open={this.state.student_accordion_open}
                        setStudentAccordionState={() => {
                          /*
                            Anytime this is called, it's toggling so I'm just setting it to the opposite of its current state. This is to sync the student accordion state with the ToggleStudentView component.  Pretty much just don't worry about it. Keep walking.
                          */
                          if (this.state.student_accordion_open) {
                            this.setState({ student_accordion_open: false })
                          } else {
                            this.setState({ student_accordion_open: true })
                          }
                        }}
                      >
                        <div className="row">
                          <div className="col-12">
                            <hr className="focus-question-heading bold-me d-none d-md-block d-lg-none d-xl-block" />

                            <h3 className="bold-me d-block d-md-none d-lg-block d-xl-none text-center">Focus Question</h3>
                            <hr className="d-block d-md-none d-lg-block d-xl-none" />

                            <p className="focus-question bold-me">What would we see if we looked closer at body tissue?</p>

                            <hr className="after-focus-question" />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-12">
                            <p>You have examined different samples of body tissue and seen that our bodies are made of much smaller building blocks. When you look at tissue, you can see a repeating pattern. While tissues can look very different from one another, there are some similar structures found in all types of tissues in the body. Watch the animation about what makes up the building blocks of these tissue samples. It's CELLS!</p>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-12">
                            <hr className="notebook-icon my-5" />
                            <span className="sr-only">Notebook Icon</span>

                            <h3 className="section-heading bold-me">Watch the Building Block animation, and then revisit your microscope observations. </h3>

                            <p className="list-heading bold-me underline-me my-4">On your own</p>
                            <ol>
                              <li>Watch the Building Block animation.</li>
                              <li>After watching the animation, revisit your observations from the Virtual Microscope found on your <span className="italic">A Closer Look: Observation Sheet</span>. Identify the cell membrane and nucleus on your diagrams and add labels as needed. If necessary, <Link to="/chapter-2/bridge/observing-tissue-with-microscopes" target="_blank" rel="noopener noreferrer">revisit the Virtual Microscope&nbsp;<sup><i  style={{fontSize: '.65rem'}}  className="fas fa-external-link-alt"></i></sup></Link> to review images of body tissues.</li>
                            </ol>
                          </div>

                          <Video vimeo_id="343310928" no_row={true} />
                        </div>
                      </TeacherContentStudentAccordion>
                    </Col>
                    <Col xs={12} lg={6} name="teacher-accordion-column">
                      <Container fluid>
                        <Row>
                          <TeacherContentTeacherAccordion title="Time Estimate" open={true}>
                            <p>Estimated time to complete this web page: 12 min</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Before You Teach" open={true}>
                            <p>Make sure that you can play the <span className="italic">Building Blocks</span> animation.</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Background Information">
                            <p className="bold-me">Cells</p>

                            <p>The cell is considered to be the smallest unit of living matter in the body and more broadly as the basic building block of life on Earth. It may be noted that viruses are smaller and simpler than cells. However, a virus is not generally considered to be alive since it can only reproduce after it infects a living cell and hijacks the cell’s machinery to make copies of itself. An individual cell is itself made up of smaller components called organelles. Each cellular organelle has a specific job to do that supports the life of the cell.</p>

                            <p>The information center of the cell is the nucleus. It contains over six feet of DNA molecules that direct the production of thousands of proteins needed to build cell structures and carry out metabolic reactions. Outside of the nucleus is a gel-like fluid called cytoplasm that contains the organelles. Each cell contains many membrane-bound organelles called mitochondria. The mitochondria are responsible for extracting energy from nutrients and saving it in the form of adenosine triphosphate (ATP) molecules. Eons ago, a mitochondrion existed as a free-living cell that entered into a symbiotic relationship with a larger cell. Over millions of years this smaller cell lost the ability to survive on its own. Mitochondria are the only organelles in animal cells, besides the nuclei, to contain DNA. The mitochondrial genome is tiny, however, consisting of about 16,000 basepairs of DNA as compared to over 3 billion basepairs in the nucleus.</p>

                            <p>The ribosome is the organelle responsible for protein synthesis. Each ribosome is composed of two subunits made of RNA and protein. An individual cell contains millions of ribosomes. When a gene in the nucleus is active, an RNA copy called a messenger RNA (mRNA) is made. The mRNA leaves the nucleus where it combines with the two subunits of a ribosome. The ribosome builds the protein by linking together different amino acids in a sequence determined by the genetic code contained in the mRNA molecule.</p>

                            <p>Each cell is bounded by a cell membrane. The cell membrane is an organelle that regulates what comes into the cell and what leaves the cell. The cell membrane is organized as a lipid bilayer containing embedded proteins. Passive, meaning nonenergy requiring, processes such as diffusion and osmosis are used to transport substances from areas of higher concentration to areas of lower concentration. These passive mechanisms transport gases (O<sub>2</sub> and CO<sub>2</sub>), ions, and small molecules into and out of the cell. Some larger organic molecules like sugars and amino acids use specialized proteins that span the membrane to enter or leave the cell. Sometimes the cell needs to transport substances against a concentration gradient. In such cases, energy from the hydrolysis of ATP molecules is used to drive the transport.</p>

                            <p>In addition to the organelles already described, cells contain other organelles directed to perform still more functions such as the storage of nutrients and the breakdown of wastes. The human body contains many different types of cells, nearly all of which use these organelles. The most notable exception is the red blood cell. Mature red blood cells have one function—to use hemoglobin to transport oxygen to the other cells of the body. To do this efficiently, mature red blood cells lose their organelles in order to make room for as much hemoglobin as possible.</p>

                            <div className="col-12">
                              <Img
                                fluid={this.props.data.image1.childImageSharp.fluid}
                                alt="Levels of Organization Illustration"
                                className="no-caption-image my-4"
                              />
                            </div>

                            <p className="bold-me">Tissues</p>

                            <p>A tissue is a collection of cells that have a similar shape and function. Not all of the cells in a specific tissue type have to be identical, but they all have the same origin during development. There are four basic types of tissue in the human body:</p>

                            <ul>
                              <li>Connective tissues are the most abundant and widely distributed tissues in the body. They provide protection and support. Examples of connective tissue include cartilage, bone, fibrous tissue, and fat. Perhaps surprisingly, blood and lymph are also considered to be connective tissue. This is because they share a common embryonic origin with the other connective tissues.</li>

                              <li>Epithelial tissues include the skin that covers the body, as well as sheets of cells that line the cavities of the digestive and respiratory systems. The walls of organs and glands also are made of epithelial tissue.</li>

                              <li>Muscle tissues come in three types. Skeletal muscle is the tissue associated with voluntary muscle movement such as flexing an arm. Smooth muscle is found in the walls of internal organs and blood vessels. Cardiac muscle is only found in the walls of the heart. Both smooth and cardiac muscles are classified as involuntary, meaning that their contractions occur without conscious effort.</li>

                              <li>Nervous tissue makes up most of the brain and spinal cord as well as the branching nerves that control body functions and activities. Nerve cells are specialized and cannot just receive signals but also can conduct impulses to and from all parts of the body.</li>
                            </ul>

                            <div className="col-12">
                              <Img
                                fluid={this.props.data.image2.childImageSharp.fluid}
                                alt="Levels of Organization Illustration"
                                className="no-caption-image mt-4"
                              />
                            </div>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Where are we in the storyline?">
                            <p>In previous lessons, students figured out that there must be something wrong with M'Kenna's small intestine. They have even seen endoscope images showing that her small intestine looks swollen. But what is really wrong? Students need to look closer at tissue samples to figure out more about M'Kenna's illness. But what would a student expect to see when they look at healthy body tissue under a microscope? This lesson helps them answer that question.</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Phenomenon for This Lesson">
                            <p>The phenomenon for this lesson is that we notice repeating patterns when we look at body tissue under a microscope.</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Main Learning Goal" open={true}>
                            <p>All body tissue is made of cells and all cells have common structures. <span className="italic">Students will meet this main learning goal by the end of the Chapter 1-2 Bridge</span>.</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Teacher Moves During Lesson: Steps 1-2" open={true}>
                            <div className="row">
                              <div className="col-12">
                                <hr style={{ margin: '10px 0' }} />
                                <div className="text-center">
                                  {/* For multiple icons: Every icon except the last one should have margin-right: 1rem; */}
                                  {/* For a single icon, leave margin-right: 1rem; out all together. */}
                                  <img src="https://media.bscs.org/3dmss/te/icons/disciplinary-core-idea-icon.svg" alt="NGSS Disciplinary Core Idea icon." style={{ width: '42px' }} />
                                </div>
                                <hr style={{ margin: '10px 0' }} />
                              </div>

                              <div className="col-12 my-4">
                                <span className="sr-only">NGSS Disciplinary Core Idea</span>
                                <h3 className="section-heading bold-me text-center">All tissue is made of cells, and cells have common structures such as a nucleus and a membrane.</h3>
                              </div>
                            </div>

                            <ol type="A">
                              <li>
                                <p>In <span className="bold-me">steps 1 and 2</span>, students watch the short animation and then return to their observation sheet from the <span className="italic">Virtual Microscope</span>. They point out and label cell membranes and nuclei on their sketches.</p>
                              </li>
                              <li>
                                <p>Move around the room and check that students understand that the cells <span className="italic">make</span> the tissue. A common misconception is that cells are "inside" the body (like marbles in a bag). It's important that students understand that cells build the body just like bricks build a wall.</p>
                              </li>
                              <li>
                                <p>You may want students to add the vocabulary word, "cell," to a personal glossary. They should write definitions of these terms in their own words. It's OK at this point if student definitions are incomplete. For example, students could define "cell" as "something that builds tissue that has a membrane and a nucleus. Cells of the same kind of tissue look alike." Also, students can include a drawing in their glossaries, if they like. English Language Learners should be encouraged to write their definitions in whatever language is most natural to them.</p>
                              </li>
                            </ol>
                          </TeacherContentTeacherAccordion>
                        </Row>
                      </Container>
                    </Col>
                  </React.Fragment>
                }
                {!this.state.authenticated &&
                  <StudentContent>
                    <div className="row">
                      <div className="col-12">
                        <hr className="focus-question-heading bold-me" />

                        <p className="focus-question bold-me">What would we see if we looked closer at body tissue?</p>

                        <hr className="after-focus-question" />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <p>You have examined different samples of body tissue and seen that our bodies are made of much smaller building blocks. When you look at tissue, you can see a repeating pattern. While tissues can look very different from one another, there are some similar structures found in all types of tissues in the body. Watch the animation about what makes up the building blocks of these tissue samples. It's CELLS!</p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <hr className="notebook-icon my-5" />
                        <span className="sr-only">Notebook Icon</span>

                        <h3 className="section-heading bold-me">Watch the Building Block animation, and then revisit your microscope observations. </h3>

                        <p className="list-heading bold-me underline-me my-4">On your own</p>
                        <ol>
                          <li>Watch the Building Block animation.</li>
                          <li>After watching the animation, revisit your observations from the Virtual Microscope found on your <span className="italic">A Closer Look: Observation Sheet</span>. Identify the cell membrane and nucleus on your diagrams and add labels as needed. If necessary, <Link to="/chapter-2/bridge/observing-tissue-with-microscopes" target="_blank" rel="noopener noreferrer">revisit the Virtual Microscope&nbsp;<sup><i  style={{fontSize: '.65rem'}}  className="fas fa-external-link-alt"></i></sup></Link> to review images of body tissues.</li>
                        </ol>
                      </div>

                      <Video vimeo_id="343310928" no_row={true} />
                    </div>
                  </StudentContent>
                }
              </Row>
              <NextPreviousRow location={location} authenticated={this.state.authenticated} />
            </Container>
          </Layout>
        </React.Fragment>
      )
    }
  }
}

export default Page

export const query = graphql`
  query {
    image1: file(relativePath: { eq: "02_vil/2_vil_0bdg_webc_te.jpg" }) {
      ...threedmssImage
    }
    image2: file(relativePath: { eq: "02_vil/2_vil_0bdg_webc_te_bodytissues.jpg" }) {
      ...threedmssImage
    }
  }
`